import { defineStore } from "pinia"
import { computed, ref, type Ref } from "vue"
import { ServiceProviderDate } from "@/models/service-provider-date.model"
import { useSocketSynchronizationHandlers } from "@/composables/socket/socket-synchronization-handlers"
import { BaseModel } from "@/models/base.model"
import { type Moment } from "moment"
import { ODataQueryBuilder } from "@/builder/odata-query-builder"
import { ServiceProviderDateRepository } from "@/repositories/service-provider-date-repository"
import { type IServiceProviderDate } from "@/interfaces/api/service-provider-date.interface"
import { ModelFactory } from "@/factories/model-factory"
import { cloneDeep } from "lodash-es"
import { type IRepositoryRequestConfig } from "@/interfaces/repository/repository-request-config.interface"

export const useServiceProviderDateStore = defineStore("serviceProviderDatesStore", () => {
  const serviceProviderDatesDict: Ref<{
    [date: string]: { [serviceProviderId: string]: ServiceProviderDate }
  }> = ref({})

  const serviceProviderDatesList = computed<ServiceProviderDate[]>(() => {
    return Object.values(serviceProviderDatesDict.value).flatMap((serviceProviderDate) => {
      return Object.values(serviceProviderDate)
    })
  })

  function addServiceProviderDateToDict(serviceProviderDate: ServiceProviderDate) {
    const dateStr = serviceProviderDate.date.format("YYYY-MM-DD")
    if (!serviceProviderDatesDict.value[dateStr]) {
      serviceProviderDatesDict.value[dateStr] = {}
    }
    serviceProviderDatesDict.value[dateStr][serviceProviderDate.serviceProvider.id] =
      serviceProviderDate
  }

  useSocketSynchronizationHandlers(
    ServiceProviderDate,
    serviceProviderDatesList,
    (model: BaseModel) => {
      const serviceProviderDate = model as ServiceProviderDate
      const dateStr = serviceProviderDate.date.format("YYYY-MM-DD")
      if (!serviceProviderDatesDict.value[dateStr]) {
        serviceProviderDatesDict.value[dateStr] = {}
      }
      serviceProviderDatesDict.value[dateStr][serviceProviderDate.serviceProvider.id] =
        serviceProviderDate
    },
    (model: BaseModel) => {
      const serviceProviderDate = model as ServiceProviderDate
      const dateStr = serviceProviderDate.date.format("YYYY-MM-DD")
      if (serviceProviderDatesDict.value[dateStr]) {
        delete serviceProviderDatesDict.value[dateStr][serviceProviderDate.serviceProvider.id]
      }
    },
    false
  )

  const datesFetched = ref<{ [date: string]: boolean | undefined }>({})
  /**
   * Fetch service provider dates for a specific date.
   */
  async function fetchServiceProviderDatesForDate(date: Moment) {
    // Fetch service provider dates for a specific date.
    const dateStr = date.clone().format("YYYY-MM-DD")

    datesFetched.value[dateStr] = true

    if (!serviceProviderDatesDict.value[dateStr]) {
      serviceProviderDatesDict.value[dateStr] = {}
    }

    const query = new ODataQueryBuilder().eq("date", dateStr)
    const result = await new ServiceProviderDateRepository().getList({ query })
    const models = ModelFactory.createModels(
      ServiceProviderDate,
      result.data as IServiceProviderDate[]
    )
    await BaseModel.whenFullyResolved(models)
    models.forEach(addServiceProviderDateToDict)
  }

  function getServiceProviderDatesForDate(date: Moment) {
    return serviceProviderDatesDict.value[date.format("YYYY-MM-DD")]
  }

  /**
   * Fetch service provider dates for a range of dates in a single HTTP request.
   * Dates that have already been fetched (or are in flight) are skipped, so
   * this is safe to call repeatedly from multiple components for overlapping
   * ranges.
   */
  async function fetchServiceProviderDatesForDates(dates: Moment[]) {
    const uniqueDateStrs = Array.from(new Set(dates.map((d) => d.format("YYYY-MM-DD"))))
    const dateStrs: string[] = []
    for (const dateStr of uniqueDateStrs) {
      if (!datesFetched.value[dateStr]) {
        dateStrs.push(dateStr)
      }
    }

    if (dateStrs.length > 0) {
      // Mark as fetched up-front so concurrent callers don't issue duplicate
      // requests; matches the behaviour of fetchServiceProviderDatesForDate.
      for (const dateStr of dateStrs) {
        datesFetched.value[dateStr] = true
        if (!serviceProviderDatesDict.value[dateStr]) {
          serviceProviderDatesDict.value[dateStr] = {}
        }
      }

      const query = new ODataQueryBuilder().in("date", dateStrs)
      const result = await new ServiceProviderDateRepository().getList({ query })
      const models = ModelFactory.createModels(
        ServiceProviderDate,
        result.data as IServiceProviderDate[]
      )
      await BaseModel.whenFullyResolved(models)
      for (const model of models) {
        addServiceProviderDateToDict(model)
      }
    }
  }

  async function updateServiceProviderDate(
    serviceProviderDate: ServiceProviderDate,
    publish = true
  ) {
    const clone = cloneDeep(serviceProviderDate)
    const config: IRepositoryRequestConfig = {}
    if (!publish) {
      config.query = { publish: false }
    }
    await new ServiceProviderDateRepository().update(serviceProviderDate.id!, clone, config)
  }

  /**
   * Check if a date fetch has been initiated. Will remain true for the lifetime of the application.
   */
  function dateHasBeenFetched(date: Moment): boolean {
    return (
      !!datesFetched.value[date.format("YYYY-MM-DD")] &&
      !!serviceProviderDatesDict.value[date.format("YYYY-MM-DD")]
    )
  }

  /**
   * Check if a service provider date exists.
   */
  function exists(date: Moment, serviceProviderId: string) {
    const dateStr = date.format("YYYY-MM-DD")
    return (
      !!serviceProviderDatesDict.value[dateStr] &&
      !!serviceProviderDatesDict.value[dateStr][serviceProviderId]
    )
  }

  /**
   * Get service provider date by date and service provider.
   */
  function getServiceProviderDate(date: Moment, serviceProviderId: string) {
    const dateStr = date.format("YYYY-MM-DD")
    if (serviceProviderDatesDict.value[dateStr]) {
      return serviceProviderDatesDict.value[dateStr][serviceProviderId]
    }
    return null
  }

  async function deleteServiceProviderDate(serviceProviderDate: ServiceProviderDate) {
    try {
      await new ServiceProviderDateRepository().delete(serviceProviderDate.id!)
    } catch (e) {
      console.log(e)
      return false
    }

    const dateStr = serviceProviderDate.date.format("YYYY-MM-DD")
    delete serviceProviderDatesDict.value[dateStr][serviceProviderDate.serviceProvider.id]
  }

  return {
    serviceProviderDatesDict,
    serviceProviderDatesList,
    dateHasBeenFetched,
    fetchServiceProviderDatesForDate,
    fetchServiceProviderDatesForDates,
    exists,
    getServiceProviderDate,
    updateServiceProviderDate,
    deleteServiceProviderDate,
    getServiceProviderDatesForDate,
    addServiceProviderDateToDict
  }
})
